import { useSearchParams } from 'react-router-dom';

import DashboardLayout from '@/components/layout/DashboardLayout';
import SecuritySettingsPanel from '@/components/security/SecuritySettingsPanel';
import ActivityPanel from '@/components/settings/ActivityPanel';
import NotificationPreferencesPanel from '@/components/settings/NotificationPreferencesPanel';
import type { NotificationCategory } from '@/components/settings/NotificationPreferencesPanel';
import SaasParamsPanel from '@/components/settings/SaasParamsPanel';
import PageHeader from '@/components/ui/PageHeader';
import { adminSidebar } from '@/config/sidebarConfig.ts';
import { Activity, Bell, Shield, SlidersHorizontal } from 'lucide-react';

// ─── Tab definitions ───────────────────────────────────────────

// Onglet FFPJP retiré (plan FFPJP auto-déclaration §11 / §13) : le référentiel
// licence n'est plus fiable, le panneau de sync ne doit pas suggérer le contraire.
type TabId = 'security' | 'saas' | 'notifications' | 'activity';

interface TabDef {
  id: TabId;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  soon?: true;
}

const TABS: TabDef[] = [
  {
    id: 'security',
    label: 'Sécurité',
    description: '2FA, appareils de confiance',
    icon: Shield,
  },
  {
    id: 'saas',
    label: 'Plateforme',
    description: 'Maintenance, inscriptions',
    icon: SlidersHorizontal,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    description: 'Alertes systeme et plateforme',
    icon: Bell,
    soon: true,
  },
  {
    id: 'activity',
    label: 'Activite',
    description: 'Journal des actions sensibles',
    icon: Activity,
  },
];

const DEFAULT_TAB: TabId = 'security';

const ADMIN_NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  {
    id: 'system_alerts',
    label: 'Alertes systeme',
    description: 'Erreurs critiques, pannes, seuils depasses',
    email: true,
  },
  {
    id: 'new_users',
    label: 'Nouveaux utilisateurs',
    description: 'Inscriptions de nouveaux organisateurs et clubs',
    email: true,
  },
  {
    id: 'payments',
    label: 'Paiements',
    description: 'Abonnements, remboursements, litiges Stripe',
    email: true,
  },
  {
    id: 'reports',
    label: 'Signalements',
    description: 'Contenus signales, comportements suspects',
    email: true,
  },
  {
    id: 'platform',
    label: 'Mises a jour plateforme',
    description: 'Deploiements, maintenances planifiees',
    email: false,
  },
];

// ─── Settings Nav ──────────────────────────────────────────────

interface SettingsNavProps {
  activeTab: TabId;
  onSelect: (tab: TabId) => void;
}

function SettingsNav({ activeTab, onSelect }: SettingsNavProps) {
  return (
    <nav className='flex flex-col gap-1 w-56 shrink-0'>
      {TABS.map(tab => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type='button'
            onClick={() => !tab.soon && onSelect(tab.id)}
            disabled={tab.soon}
            className={`flex items-start gap-3 w-full px-3 py-3 rounded-lg text-left transition-colors group
              ${isActive ? 'bg-red-50 text-red-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}
              ${tab.soon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <Icon
              className={`w-4 h-4 mt-0.5 shrink-0 ${isActive ? 'text-red-600' : 'text-slate-400 group-hover:text-slate-600'}`}
            />
            <div className='min-w-0'>
              <div className='flex items-center gap-2'>
                <span className={`text-sm font-semibold ${isActive ? 'text-red-700' : ''}`}>{tab.label}</span>
                {tab.soon && (
                  <span className='text-[10px] font-bold uppercase tracking-wide bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded'>
                    bientôt
                  </span>
                )}
              </div>
              <p className='text-xs text-slate-400 mt-0.5 truncate'>{tab.description}</p>
            </div>
          </button>
        );
      })}
    </nav>
  );
}

// ─── Panel content ─────────────────────────────────────────────

function TabContent({ tab }: { tab: TabId }) {
  switch (tab) {
    case 'security':
      return <SecuritySettingsPanel />;
    case 'saas':
      return <SaasParamsPanel />;
    case 'notifications':
      return <NotificationPreferencesPanel categories={ADMIN_NOTIFICATION_CATEGORIES} accentColor='red' />;
    case 'activity':
      return <ActivityPanel />;
    default:
      return null;
  }
}

// ─── Main ──────────────────────────────────────────────────────

export default function AdminSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as TabId | null;
  const activeTab: TabId = rawTab && TABS.some(t => t.id === rawTab && !t.soon) ? rawTab : DEFAULT_TAB;

  function handleSelect(tab: TabId) {
    setSearchParams({ tab }, { replace: true });
  }

  const currentTab = TABS.find(t => t.id === activeTab)!;

  return (
    <DashboardLayout sidebarConfig={adminSidebar}>
      <div className='p-8'>
        <PageHeader title='Paramètres' subtitle='Configuration et sécurité de la plateforme' />

        <div className='flex gap-8 mt-6'>
          {/* Left nav */}
          <div className='shrink-0'>
            <SettingsNav activeTab={activeTab} onSelect={handleSelect} />
          </div>

          {/* Divider */}
          <div className='w-px bg-slate-200 self-stretch' />

          {/* Content */}
          <div className='flex-1 min-w-0'>
            <div className='mb-6'>
              <h2 className='text-xl font-bold text-slate-900'>{currentTab.label}</h2>
              <p className='text-sm text-slate-500 mt-0.5'>{currentTab.description}</p>
            </div>
            <TabContent tab={activeTab} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
